import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Icon, Button, NakedButton, MelioLogo, type IconKey } from '@/components/ds';
import { completeLogin, startLogin } from '@/api/auth';
import type { Go } from '@/lib/types';

// signin.tsx — dedicated full-page sign-in. Credentials, SSO and MFA are all owned by the central
// Melio auth service: every button here leaves for `/api/auth/login`, which redirects to
// auth-service's authorize endpoint. "Continue with Google" carries `sso_provider=google`, which the
// account screens use to start Google SSO straight away instead of rendering their own form;
// "Continue with email" lands on those screens. auth-service then returns to /signin/callback,
// which posts the code to the backend for the exchange.

const CALLBACK_ERRORS: Record<string, string> = {
  access_denied: 'Sign in was cancelled. Try again to continue.',
  invalid_request: 'That sign-in link expired. Start again to continue.',
  no_session: 'We could not start your session. Try again to continue.',
  exchange_failed: 'We could not complete your sign in. Try again to continue.',
  // The backend's code for "auth-service rejected/could not serve the token exchange".
  AUTH_SERVICE_TOKEN_ERROR: 'We could not complete your sign in. Try again to continue.',
  unsupported_sso_provider: 'That sign-in method is not supported. Use your email and password instead.',
  // No entry for `email_not_allowed` (an account the backend's allowlist refuses): SignInRoute never
  // renders this page for it — it signs out of accounts and starts the sign-in over there instead.
};

const errorMessage = (code: string | null): string | undefined =>
  code ? (CALLBACK_ERRORS[code] ?? 'Sign in failed. Try again to continue.') : undefined;

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62Z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18Z"
    />
    <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34Z" />
    <path
      fill="#EA4335"
      d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58Z"
    />
  </svg>
);

interface SignInProps {
  go: Go;
  style?: CSSProperties;
}

function SignIn({ go }: SignInProps) {
  const params = new URLSearchParams(window.location.search);
  const error = errorMessage(params.get('error'));
  const returnTo = params.get('returnTo') ?? undefined;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* slim chrome — full-width app bar, matches the main TopNav across every page */}
      <div style={{ borderBottom: '1px solid var(--border-static)', flexShrink: 0 }}>
        <div style={{ padding: '0 28px', height: 64, display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => go('/')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <MelioLogo height={22} />
            <span style={{ width: 1, height: 18, background: 'var(--border-static)' }} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>API</span>
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>New here?</span>
          <NakedButton color="var(--accent-text)" style={{ marginLeft: 12 }} onClick={() => startLogin({ screen: 'signup', returnTo })}>
            Create an account
          </NakedButton>
        </div>
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
        {/* left: sign-in entry points */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 40px' }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            <h1 style={{ margin: 0, fontSize: 26, letterSpacing: '-0.02em' }}>Sign in to Melio API</h1>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
              Access your dashboard, keys, and payment activity.
            </p>

            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--text-critical)',
                  marginBottom: 18,
                }}
              >
                <Icon name="info-fill" size={15} color="var(--text-critical)" />
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={() => startLogin({ ssoProvider: 'google', returnTo })}
              style={{
                width: '100%',
                height: 48,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--text-primary)',
                background: '#fff',
                border: '1.5px solid var(--border-interactive)',
                borderRadius: 12,
                cursor: 'pointer',
              }}
            >
              <GoogleMark /> Continue with Google
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
              <span style={{ flex: 1, height: 1, background: 'var(--border-static)' }} />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>or</span>
              <span style={{ flex: 1, height: 1, background: 'var(--border-static)' }} />
            </div>

            <Button fullWidth size="large" rightIcon="arrow-right" onClick={() => startLogin({ returnTo })}>
              Continue with email
            </Button>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginTop: 20,
                fontSize: 13.5,
                color: 'var(--text-secondary)',
              }}
            >
              <span>Don&apos;t have an account?</span>
              <NakedButton color="var(--accent-text)" onClick={() => startLogin({ screen: 'signup', returnTo })}>
                Create one
              </NakedButton>
            </div>

            <p style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)', margin: '26px 0 0' }}>
              Your Melio account signs you in to the developer portal — the same email and password you use across Melio.
            </p>
          </div>
        </div>

        {/* right brand panel */}
        <div
          style={{
            background: 'linear-gradient(155deg, #1c1428, #16171f 70%)',
            padding: '56px 48px',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              right: -90,
              top: -70,
              width: 320,
              height: 320,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.35), transparent 70%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: -70,
              bottom: -90,
              width: 280,
              height: 280,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.18), transparent 70%)',
            }}
          />
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <h2 style={{ fontSize: 30, lineHeight: '38px', letterSpacing: '-0.02em', margin: '0 0 14px', color: '#fff' }}>Welcome back</h2>
            <p style={{ fontSize: 15, lineHeight: '23px', color: '#9aa3b2', margin: '0 0 36px' }}>
              Pick up where you left off — keys, webhooks, and every payment moving through your account.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {(
                [
                  ['dashboard', 'Live payment activity', 'Track ACH and check payments end to end.'],
                  ['lock', 'Manage your API keys', 'Rotate, scope, and revoke keys in seconds.'],
                  ['fast', 'Test & live in one place', 'Switch environments without losing context.'],
                ] as [IconKey, string, string][]
              ).map(([ic, h, s]) => (
                <div key={h} style={{ display: 'flex', gap: 14 }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      flexShrink: 0,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={ic} size={19} color="#c9b8ff" />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{h}</div>
                    <div style={{ fontSize: 13, lineHeight: '19px', color: '#9aa3b2', marginTop: 2 }}>{s}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 36, fontSize: 13, color: '#9aa3b2' }}>
              <Icon name="lock" size={15} color="#9aa3b2" /> Bank-grade encryption · SOC 2 Type II
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CallbackScreen({ go, children }: { go: Go; children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ borderBottom: '1px solid var(--border-static)', flexShrink: 0 }}>
        <div style={{ padding: '0 28px', height: 64, display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => go('/')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <MelioLogo height={22} />
            <span style={{ width: 1, height: 18, background: 'var(--border-static)' }} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>API</span>
          </button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 40px' }}>
        <div style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>{children}</div>
      </div>
    </div>
  );
}

/**
 * Where auth-service returns the browser to. Posts the `code` to the backend for the exchange,
 * then reloads into the portal so the app state hydrates from the fresh session cookie.
 */
function SignInCallback({ go }: SignInProps) {
  // The authorization code is single-use. StrictMode re-runs effects in dev (and a remount could too),
  // so guard the exchange to fire exactly once — a second POST spends the code and 400s INVALID_GRANT.
  const exchanged = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const failed = (error: string) => window.location.replace(`/signin?error=${encodeURIComponent(error)}`);

    const providerError = params.get('error');
    const code = params.get('code');
    const state = params.get('state');
    if (providerError || !code || !state) return failed(providerError ?? 'invalid_request');

    if (exchanged.current) return;
    exchanged.current = true;

    completeLogin(code, state).then(
      (returnTo) => window.location.replace(returnTo),
      (error: Error) => failed(error.message)
    );
  }, []);

  return (
    <CallbackScreen go={go}>
      <p style={{ fontSize: 14.5, color: 'var(--text-secondary)', margin: 0 }}>Completing your sign in…</p>
    </CallbackScreen>
  );
}

export { SignIn, SignInCallback };
